import AppConstants from "constants/AppConstants";
import { isInputAnArray } from "./helpers.utils";
import { ClubbingSuggestions, Metadata, DeliveryReason, DeliveryReasonWithComment, Item } from "./utiles.types";
import { isNonEmptyArray } from "./lodash.utils";
import { VEHICLE_LABEL_MAP } from "constants/CommonConstants";

/**
 * Returns a human-readable label for a vehicle type.
 * Uses VEHICLE_LABEL_MAP for known types; falls back to capitalising the first
 * character and lowercasing the remainder for unknown types.
 *
 * @param vehicle - Upper-case vehicle type string (e.g. "BIKE", "CAR", "BICYCLE")
 * @returns Display label (e.g. "Bike", "Car", "Bicycle")
 */
export const getVehicleLabel = (vehicle: string): string =>
  VEHICLE_LABEL_MAP[vehicle] ?? vehicle.charAt(0) + vehicle.slice(1).toLowerCase();

/**
 * Determines the default vehicle type to pre-select from a clubbing configuration.
 * Prefers "CAR" when present; otherwise returns the first vehicle type encountered.
 * Returns an empty string when the clubbing map is empty.
 *
 * @param clubbing - Map of delivery-type keys to vehicle-type/limit pairs
 *                   e.g. { EXPRESS: { CAR: 5, BIKE: 3 }, STANDARD: { CAR: 4 } }
 * @returns The selected vehicle type string, or "" if none exist
 */
export const getDefaultVehicleType = (clubbing: Record<string, Record<string, number>>): string => {
  const allVehicles = Array.from(new Set(
    Object.values(clubbing).flatMap((v) => Object.keys(v))
  ));
  return allVehicles.includes("CAR") ? "CAR" : allVehicles[0] || "";
};

/**
 * Checks if either `bundledProducts` or `virtualProducts` is true in the provided metadata.
 *
 * @param {Metadata} metadata - The metadata object containing `bundledProducts` and `virtualProducts` properties.
 * @returns {boolean} - Returns true if either `bundledProducts` or `virtualProducts` is true, otherwise false.
 */
export const isBundleOrVirtualProduct = (metadata: Metadata): boolean => {
    return !!metadata?.bundledProducts || !!metadata?.virtualProducts;
  };

export const isRestrictedAction = (restrictions: string[], restrictionType: string): boolean => {
    return restrictions.includes(restrictionType);
};  

/**
 * Checks if a store/hub is enabled based on config details
 * @param storeCode - The store code to check
 * @param configDetails - Array of config objects with value and active properties
 * @returns boolean indicating if the store is enabled
 */
export const isStoreEnabledInConfig = (
  storeCode: string,
  configDetails: Array<{ value?: string; active?: boolean }> | undefined
): boolean => {
  const config = configDetails?.[0];
  
  if (!config?.value || !config.active || !storeCode) return false;
  
  const value = config.value.trim().toUpperCase();
  // Handle "All" case
  if (value === "ALL") return true;
  
  // Handle comma-separated values
  const enabledStores = value.split(",").map((store: string) => store.trim().toUpperCase());
  return enabledStores.includes(storeCode.toUpperCase());
};

/**
 * Checks if clubbing suggestions exist and have valid orders based on rtdBreached status.
 *
 * @param {ClubbingSuggestions | undefined} clubbingSuggestions - The clubbing suggestions object containing orders array.
 * @param {boolean | undefined} rtdBreached - Flag indicating if Ready-to-Deliver time is breached.
 * @returns {boolean} - Returns true if rtdBreached is true and there is at least 1 order, 
 *                      or if rtdBreached is false/undefined and there are more than 1 order. 
 *                      Returns false if orders is not an array or conditions are not met.
 */
export const hasValidClubbingSuggestions = (
  clubbingSuggestions?: ClubbingSuggestions,
  rtdBreached?: boolean
): boolean => {
  // Return false if orders are not an array
  if (!isInputAnArray(clubbingSuggestions?.orders)) {
    return false;
  }

  const ordersLength = clubbingSuggestions?.orders?.length ?? 0;
  
  // If rtdBreached is true, check if ordersLength > 0
  if (rtdBreached === true) {
    return ordersLength > 0;
  }

  // Otherwise, check if ordersLength > 1 (original behavior)
  return ordersLength > 1;
};
export const isSubstitutedProducts = (item: Item): boolean => {
  return !!item?.substitutedProducts && !!isNonEmptyArray(item.substitutedProducts);
}

export const getSelectedDeliveryReasonWithComment = (
    reasonCode: string,
    comment: string,
    deliveryReasons: DeliveryReason[],
): DeliveryReasonWithComment | null => {
    if (reasonCode === AppConstants.SELECT_NONE.value || !deliveryReasons?.length) {
        return null;
    }

    const deliveryReason = deliveryReasons.find((reason) => reason.reasonCode === reasonCode);
    
    if (deliveryReason) {
        return { ...deliveryReason, comment };
    }
    
    return null;
};